<script setup lang="ts">
import OrgActivityCard from '@/components/admin/OrgActivityCard.vue'
import OrgActivityDrawer from '@/components/admin/OrgActivityDrawer.vue'
import type { Modules } from '@/types/generated'

type OrgActivityCardData = Modules.Analytics.Data.OrgActivityCardData
type Scope = 'today' | 'week' | 'all' | 'inactive'
type SortKey = 'recent' | 'least_active' | 'az'

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()

const scope = ref<Scope>('week')
const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const sortBy = ref<SortKey>('recent')

const drawerOpen = ref(false)
const drawerOrgId = ref<string | null>(null)
const drawerOrgName = ref('')

const { data, isFetching, error, execute } = await useApi<{ data: OrgActivityCardData[] }>(
  createUrl('/admin/org-activity/', {
    query: {
      scope,
      search: debouncedSearch,
    },
  }),
).json()

const cards = computed<OrgActivityCardData[]>(() => data.value?.data ?? [])

const sortedCards = computed<OrgActivityCardData[]>(() => {
  const list = [...cards.value]

  if (sortBy.value === 'least_active') {
    list.sort((a, b) => a.totalPlays7d - b.totalPlays7d)
  }
  else if (sortBy.value === 'az') {
    list.sort((a, b) => a.organizationName.localeCompare(b.organizationName))
  }
  else {
    list.sort((a, b) => {
      const aTs = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0
      const bTs = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0

      return bTs - aTs
    })
  }

  return list
})

const totalCount = computed(() => sortedCards.value.length)

function setScope(next: Scope) {
  scope.value = next
}

function openDrawer(card: OrgActivityCardData) {
  drawerOrgId.value = card.organizationId
  drawerOrgName.value = card.organizationName
  drawerOpen.value = true
}
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap align-center gap-3 py-4">
        <div class="d-flex flex-wrap gap-2">
          <VChip
            :variant="scope === 'today' ? 'flat' : 'outlined'"
            :color="scope === 'today' ? 'primary' : undefined"
            @click="setScope('today')"
          >
            {{ t('org_activity.chips.today') }}
          </VChip>
          <VChip
            :variant="scope === 'week' ? 'flat' : 'outlined'"
            :color="scope === 'week' ? 'primary' : undefined"
            @click="setScope('week')"
          >
            {{ t('org_activity.chips.week') }}
          </VChip>
          <VChip
            :variant="scope === 'all' ? 'flat' : 'outlined'"
            :color="scope === 'all' ? 'primary' : undefined"
            @click="setScope('all')"
          >
            {{ t('org_activity.chips.all') }}
          </VChip>
          <VChip
            :variant="scope === 'inactive' ? 'flat' : 'outlined'"
            :color="scope === 'inactive' ? 'primary' : undefined"
            @click="setScope('inactive')"
          >
            {{ t('org_activity.chips.inactive') }}
          </VChip>
        </div>

        <VSpacer />

        <VTextField
          v-model="searchQuery"
          clearable
          density="compact"
          :placeholder="t('org_activity.search_placeholder')"
          prepend-inner-icon="tabler-search"
          hide-details
          style="min-inline-size: 240px; max-inline-size: 320px;"
        />

        <AppSelect
          v-model="sortBy"
          density="compact"
          hide-details
          :items="[
            { value: 'recent', title: t('org_activity.sort.recent') },
            { value: 'least_active', title: t('org_activity.sort.least_active') },
            { value: 'az', title: t('org_activity.sort.az') },
          ]"
          style="min-inline-size: 180px; max-inline-size: 220px;"
        />
      </VCardText>
    </VCard>

    <div class="mt-4">
      <VAlert
        v-if="error"
        type="error"
        class="mb-4"
      >
        {{ error }}
        <template #append>
          <VBtn
            size="small"
            variant="text"
            @click="execute"
          >
            {{ t('retry') }}
          </VBtn>
        </template>
      </VAlert>

      <div
        v-if="isFetching && !cards.length"
        class="d-flex justify-center py-12"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </div>

      <div
        v-else-if="!sortedCards.length"
        class="text-center text-medium-emphasis py-12"
      >
        {{ t('org_activity.empty') }}
      </div>

      <VRow v-else>
        <VCol
          v-for="card in sortedCards"
          :key="card.organizationId"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <OrgActivityCard
            :card="card"
            @click="openDrawer(card)"
          />
        </VCol>
      </VRow>

      <div
        v-if="totalCount > 0"
        class="text-caption text-medium-emphasis mt-4 text-center"
      >
        {{ totalCount }}
      </div>
    </div>

    <OrgActivityDrawer
      v-model="drawerOpen"
      :organization-id="drawerOrgId"
      :organization-name="drawerOrgName"
    />
  </div>
</template>
